import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { REMINDER_CONSTRAINTS } from '@/components/atoms/ReminderTimeSelect/ReminderTimeSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  buildReminderNode,
  collectChildrenForReparenting,
  getBranchLabel,
  getConditionLabelsForTimer,
} from '@/redux/dialogs/helper';
import { selectDraftDialogNodes } from '@/redux/dialogs/selectors';
import { popModal } from '@/redux/modals/actions';
import { addAction, addNode, updateNode } from '@/redux/nodes/actions';
import { ReminderAction } from '@/shared/types/action';
import { formatSecondsToReadable } from '@/util/dateUtils';

export type ReuseTimerNodeDialogProps = {
  candidates: { nodeId: string }[];
  pendingAction: ReminderAction;
  target: {
    nodeId: string;
    beforeActionId: string | null;
    requisiteIndex: number | null;
    conditionIndex: number | null;
  };
};

export const ReuseTimerNodeDialog = ({
  candidates,
  pendingAction,
  target,
}: ReuseTimerNodeDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const draftDialogNodes = useSelector(selectDraftDialogNodes);

  const parentNode = useMemo(
    () => draftDialogNodes.find((n) => n.node_id === target.nodeId),
    [draftDialogNodes, target.nodeId]
  );

  const candidatesWithLabels = useMemo(
    () =>
      candidates.map((c) => {
        const node = draftDialogNodes.find((n) => n.node_id === c.nodeId);
        const labels = getConditionLabelsForTimer(c.nodeId, parentNode);
        const summary = labels
          .map(({ conditionName, conditionIndex, reminderSeconds }) => {
            const branchLabel = getBranchLabel(conditionName, conditionIndex, t);
            return `${branchLabel} (${formatSecondsToReadable(reminderSeconds)})`;
          })
          .join(', ');
        return { nodeId: c.nodeId, name: node?.name ?? '', summary };
      }),
    [candidates, draftDialogNodes, parentNode, t]
  );

  const incomingSec = pendingAction.reminder_seconds ?? REMINDER_CONSTRAINTS.DEFAULT_SECONDS;
  const incomingDurationLabel = formatSecondsToReadable(incomingSec);

  const [selectedNodeId, setSelectedNodeId] = useState(candidates[0]?.nodeId);

  const handleClose = useCallback(() => {
    dispatch(popModal());
  }, [dispatch]);

  const handleReuse = useCallback(() => {
    if (!selectedNodeId) {
      return;
    }
    dispatch(
      addAction({ ...target, newAction: { ...pendingAction, trigger_node_id: selectedNodeId } })
    );
    dispatch(popModal());
  }, [dispatch, pendingAction, selectedNodeId, target]);

  const handleCreateNew = useCallback(() => {
    const timerNode = buildReminderNode(target.nodeId, incomingSec, t);
    const newAction: ReminderAction = { ...pendingAction, trigger_node_id: timerNode.node_id };

    dispatch(addNode({ node: timerNode }));
    collectChildrenForReparenting(draftDialogNodes, target.nodeId).forEach((n) => {
      dispatch(updateNode({ nodeId: n.node_id, node: { parent_id: timerNode.node_id } }));
    });
    dispatch(addAction({ ...target, newAction }));
    dispatch(popModal());
  }, [dispatch, draftDialogNodes, incomingSec, pendingAction, t, target]);

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('ai_agents.reminder.reuse_timer.title', 'Reuse existing target node?')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'ai_agents.reminder.reuse_timer.description',
              "This branch will wait {{0}}. Reusing an existing target means after each branch's wait, all branches flow into the same downstream node. Pick a target to reuse, or create a separate one.",
              { 0: incomingDurationLabel }
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-2">
          {candidatesWithLabels.map((candidate) => {
            const isSelected = candidate.nodeId === selectedNodeId;
            return (
              <button
                key={candidate.nodeId}
                type="button"
                aria-pressed={isSelected}
                onClick={() => setSelectedNodeId(candidate.nodeId)}
                className={cn(
                  'flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left transition-colors',
                  isSelected
                    ? 'border-surface-button-primary bg-highlight-background'
                    : 'border-stroke hover:bg-highlight-background'
                )}
              >
                <span className="text-sm font-medium">{candidate.name}</span>
                {candidate.summary && (
                  <span className="text-xs text-foreground-muted">
                    {t('ai_agents.reminder.reuse_timer.from', 'From {{0}}', {
                      0: candidate.summary,
                    })}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleCreateNew}>
            {t('ai_agents.reminder.reuse_timer.create', 'Create new')}
          </Button>
          <Button type="button" onClick={handleReuse} disabled={!selectedNodeId}>
            {t('ai_agents.reminder.reuse_timer.use_existing', 'Use existing')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReuseTimerNodeDialog;
